import {
  ref,
  onChildChanged,
  onChildAdded,
  off,
  query,
  limitToLast,
  type DatabaseReference,
  type Unsubscribe,
} from 'firebase/database';
import { rtdb } from '@/config/firebase';
import type { RTDBEventValue, RTDBListenerConfig } from '@/types/rtdb';

/** Sanitize a string for use as an RTDB path segment */
export function sanitizeForPath(value: string): string {
  return value.replace(/[^a-zA-Z0-9\-_]/g, '-');
}

/** Reverse the sanitization to recover a conversation ID */
export function desanitizeConversationId(
  sanitizedId: string,
  appCodeTPA: string
): string {
  let convId = sanitizedId.replaceAll('--', '::');
  if (convId.includes('whatsapp::') && appCodeTPA === 'twilio') {
    convId = convId.replaceAll('whatsapp::', 'whatsapp:+');
  }
  // RTDB keys encode '+' as '-' in phone numbers.
  // Restore '+' for both plain numbers (-12345 → +12345) and
  // prefixed numbers (whatsapp:-12345 → whatsapp:+12345).
  convId = convId.split('::').map(part => {
    // WhatsApp username user ID (Meta BSUID): the server encodes its '.' as '-'.
    // "US-1349…" / "US-ENT-1349…" → "US.1349…" / "US.ENT.1349…". Phone parts
    // never contain letters, so they cannot match.
    if (/^[A-Z]{2}-(ENT-)?[A-Za-z0-9]{1,128}$/.test(part)) {
      return part.replace(/-/g, '.');
    }
    // Plain E.164: -12345 → +12345
    if (/^-\d/.test(part)) {
      return '+' + part.slice(1);
    }
    // Whatsapp prefixed: whatsapp:-12345 → whatsapp:+12345
    if (/^whatsapp:-\d/.test(part)) {
      return 'whatsapp:+' + part.slice(10);
    }
    return part;
  }).join('::');
  return convId;
}

type RTEventCallback = (conversationId: string, event: RTDBEventValue) => void;

/**
 * Manages RTDB listeners for real-time message and conversation events.
 *
 * Uses a single always-on listener at the user/org conversation-alerts path.
 * The parent-level `onChildChanged`/`onChildAdded` catches events for ALL
 * conversations — both the active chat and background ones — so there is no
 * need to tear down and reattach when switching conversations.
 */
export class RTDBListenerManager {
  /** User-level path ref (all extensions) */
  private convRef: DatabaseReference | null = null;
  /** Org-level path ref (non-WhatsApp extensions) */
  private orgConvRef: DatabaseReference | null = null;

  private unsubscribers: Unsubscribe[] = [];
  private convInitialLoadDone = false;
  private orgConvInitialLoadDone = false;

  /** Detach all active listeners */
  cleanup(): void {
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
    if (this.convRef) off(this.convRef);
    if (this.orgConvRef) off(this.orgConvRef);
    this.convRef = null;
    this.orgConvRef = null;
    this.convInitialLoadDone = false;
    this.orgConvInitialLoadDone = false;
  }

  /** Returns true if listeners are already attached */
  get isAttached(): boolean {
    return !!(this.convRef || this.orgConvRef);
  }

  /**
   * Attach listeners. Stays on for the lifetime of the session — receives
   * events for every conversation, regardless of which view is active.
   * Skips if already attached.
   *
   * Listener pattern matches the legacy app:
   *   - `onChildChanged` is UNBOUNDED — fires for value changes to any
   *     child, regardless of position. Required because conversation IDs
   *     here are sanitised phone-number pairs (alphabetic, not
   *     chronological), so a key-ordered `limitToLast(N)` would silently
   *     drop modifications to conversations whose keys aren't lex-last.
   *   - `onChildAdded` is bounded with `limitToLast(1)` — avoids the
   *     historical-replay storm of one callback per existing child on
   *     attach. Only the lex-last existing key fires once (suppressed via
   *     `convInitialLoadDone` flag), and the window slides forward as new
   *     keys arrive.
   *
   * Path-size cleanup is handled out-of-band by the
   * `cleanup_realtime_alerts` proxy-CF sub-action, fired once per page
   * load from the L2 background sync (use-app-init.ts, +10s after page
   * load). It bulk-deletes entries older than 2 minutes server-side via
   * an admin-SDK indexed query, so the unbounded-onChildChanged sync
   * doesn't carry historical bloat for long.
   */
  attach(config: RTDBListenerConfig, onEvent: RTEventCallback): void {
    // Already listening — skip
    if (this.convRef || this.orgConvRef) return;

    console.log('[RTDB] Attaching listeners');
    const { extensionName, extensionNameBase, userId, orgId, isWhatsApp } = config;

    // --- User-level path ---
    const path = `/instantUserConversationMessageAlerts/${extensionNameBase}forpage/${userId}/`;
    this.convRef = ref(rtdb, path);

    const unsub1 = onChildChanged(this.convRef, (snapshot) => {
      if (snapshot.key) onEvent(snapshot.key, snapshot.val() as RTDBEventValue);
    }, (err) => {
      console.error('[RTDB] conv onChildChanged error:', err.message);
    });
    this.unsubscribers.push(unsub1);

    const limitedQuery = query(this.convRef, limitToLast(1));
    // Allow 2s for initial snapshot to load, then treat all new events as real
    setTimeout(() => { this.convInitialLoadDone = true; }, 2000);
    const unsub2 = onChildAdded(limitedQuery, (snapshot) => {
      if (this.convInitialLoadDone && snapshot.key) {
        onEvent(snapshot.key, snapshot.val() as RTDBEventValue);
      }
    }, (err) => {
      console.error('[RTDB] conv onChildAdded error:', err.message);
    });
    this.unsubscribers.push(unsub2);

    // --- Org-level path (non-WhatsApp only) ---
    if (orgId && !isWhatsApp) {
      const sanitizedOrgId = sanitizeForPath(orgId);
      const orgPath = `/conversationMessageAlerts/${extensionName}/${sanitizedOrgId}/`;
      this.orgConvRef = ref(rtdb, orgPath);

      const unsub3 = onChildChanged(this.orgConvRef, (snapshot) => {
        if (snapshot.key) onEvent(snapshot.key, snapshot.val() as RTDBEventValue);
      }, (err) => {
        console.error('[RTDB] org conv onChildChanged error:', err.message);
      });
      this.unsubscribers.push(unsub3);

      const orgLimitedQuery = query(this.orgConvRef, limitToLast(1));
      setTimeout(() => { this.orgConvInitialLoadDone = true; }, 2000);
      const unsub4 = onChildAdded(orgLimitedQuery, (snapshot) => {
        if (this.orgConvInitialLoadDone && snapshot.key) {
          onEvent(snapshot.key, snapshot.val() as RTDBEventValue);
        }
      }, (err) => {
        console.error('[RTDB] org conv onChildAdded error:', err.message);
      });
      this.unsubscribers.push(unsub4);
    }
  }
}
